export const CHECKOUT_COMPLETION_REDIRECT_TARGET =
  'https://signup.daricestrategies.com/?checkout=completed&session_id={CHECKOUT_SESSION_ID}';

export const AUTH_PORTAL_CLARUS_LOGIN_URL =
  'https://auth.daricestrategies.com/login?return_to=https%3A%2F%2Fclarus.daricestrategies.com%2F';

export type SignupDestinationApp = 'clarus';

export interface SignupOfferRouteMetadataDto {
  destinationApp: SignupDestinationApp;
  offerRouteKey: string;
}

export interface SignupOfferDisplayDto {
  title: string;
  priceLabel: string;
  description: string;
  paymentLinkUrl: string;
  routeMetadata: SignupOfferRouteMetadataDto;
}

const PAYMENT_LINK_RE = /^https:\/\/(?:buy\.stripe\.com\/[A-Za-z0-9_/-]+|checkout\.daricestrategies\.com\/b\/[A-Za-z0-9_/-]+)(?:\?[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]*)?$/u;
const ROUTE_KEY_RE = /^[a-z0-9][a-z0-9-]{1,63}$/u;

export function parseSignupOfferDisplayConfigDto(raw: unknown): SignupOfferDisplayDto[] {
  if (!Array.isArray(raw)) throw new Error('FAIL: SignupOfferDisplayConfigDto must be an array');
  if (raw.length === 0) throw new Error('FAIL: SignupOfferDisplayConfigDto must contain at least one offer');
  return raw.map((entry, index) => parseSignupOfferDisplayDto(entry, index));
}

export function readSignupOffersFromEnv(): SignupOfferDisplayDto[] {
  const raw = import.meta.env.VITE_SIGNUP_OFFERS_JSON;
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('FAIL: VITE_SIGNUP_OFFERS_JSON must be valid JSON');
  }
  return parseSignupOfferDisplayConfigDto(parsed);
}

function parseSignupOfferDisplayDto(raw: unknown, index: number): SignupOfferDisplayDto {
  if (!isRecord(raw)) throw new Error(`FAIL: signup offer ${index} must be an object`);
  const routeMetadata = parseSignupOfferRouteMetadataDto(raw, index);
  const paymentLinkUrl = requiredString(raw, 'paymentLinkUrl', index);
  if (!PAYMENT_LINK_RE.test(paymentLinkUrl)) {
    throw new Error(`FAIL: signup offer ${index} paymentLinkUrl must be a Stripe Payment Link URL`);
  }
  return {
    title: requiredString(raw, 'title', index),
    priceLabel: requiredString(raw, 'priceLabel', index),
    description: requiredString(raw, 'description', index),
    paymentLinkUrl,
    routeMetadata,
  };
}

function parseSignupOfferRouteMetadataDto(raw: unknown, index: number): SignupOfferRouteMetadataDto {
  if (!isRecord(raw)) throw new Error(`FAIL: signup offer ${index} routeMetadata must be an object`);
  const destinationApp = requiredString(raw, 'destinationApp', index);
  if (destinationApp !== 'clarus') {
    throw new Error(`FAIL: signup offer ${index} destinationApp must be clarus`);
  }
  const offerRouteKey = requiredString(raw, 'routeKey', index);
  if (!ROUTE_KEY_RE.test(offerRouteKey)) {
    throw new Error(`FAIL: signup offer ${index} routeKey must be a stable route key`);
  }
  return { destinationApp, offerRouteKey };
}

function requiredString(input: Record<string, unknown>, field: string, index: number): string {
  const value = input[field];
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`FAIL: signup offer ${index} ${field} must be a non-empty string`);
  }
  return value.trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
